import React, { useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/**
 * A small map + address search for picking a coordinate. Click the map (or drag the pin) to
 * place it, or type an address/place and pick a result (geocoded via OpenStreetMap
 * Nominatim — no API key). Reports the chosen lat/lon up via onPick, rounded to 5 decimals
 * (~1 m). The parent owns lat/lon, so typed coordinates move the pin (two-way).
 *
 * The search box is deliberately NOT a <form>: this picker is embedded in admin dialogs
 * that are forms themselves, and React bubbles a nested form's submit to the outer one —
 * pressing Enter in the search used to submit (save) the surrounding dialog.
 */

const pinIcon = L.divIcon({
  className: "",
  html: `<div style="width:16px;height:16px;border-radius:50% 50% 50% 0;
      background:#3b82f6;border:2px solid #fff;transform:rotate(-45deg);
      box-shadow:0 1px 4px rgba(0,0,0,.5);"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

interface NominatimResult {
  display_name: string;
  lat: string;
  lon: string;
}

interface Props {
  lat: number | null;
  lon: number | null;
  onPick: (lat: number, lon: number) => void;
  heightPx?: number;
}

/** Recenters the map when the picked point changes from outside (e.g. a search result). */
const Recenter: React.FC<{ lat: number | null; lon: number | null }> = ({ lat, lon }) => {
  const map = useMap();
  React.useEffect(() => {
    if (lat != null && lon != null) map.setView([lat, lon], Math.max(map.getZoom(), 9));
  }, [lat, lon]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

/** ~1 m precision; also wraps a longitude dragged past the antimeridian back into range. */
export const roundCoord = (v: number): number => Number(v.toFixed(5));
const wrapLon = (lon: number): number => ((((lon + 180) % 360) + 360) % 360) - 180;

const ClickToPick: React.FC<{ onPick: Props["onPick"] }> = ({ onPick }) => {
  useMapEvents({
    click: (e) => onPick(roundCoord(e.latlng.lat), roundCoord(wrapLon(e.latlng.lng))),
  });
  return null;
};

const LocationPickerMap: React.FC<Props> = ({ lat, lon, onPick, heightPx = 300 }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = async () => {
    const term = q.trim();
    if (!term) return;
    setSearching(true);
    setError(null);
    try {
      // Public Nominatim endpoint — no key. Rate-limited (~1 req/s), fine for admin use.
      const url =
        "https://nominatim.openstreetmap.org/search?format=json&limit=5&q=" +
        encodeURIComponent(term);
      const resp = await fetch(url, { headers: { Accept: "application/json" } });
      if (!resp.ok) throw new Error(String(resp.status));
      const data = (await resp.json()) as NominatimResult[];
      setResults(data);
      if (data.length === 0) setError("No matches.");
      else onPick(roundCoord(Number(data[0].lat)), roundCoord(Number(data[0].lon)));
    } catch {
      setError("Address lookup failed. Click the map to place the pin instead.");
    } finally {
      setSearching(false);
    }
  };

  const center: [number, number] = lat != null && lon != null ? [lat, lon] : [30, 0];

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          aria-label="Search address or place"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              // Never let Enter reach a surrounding form.
              e.preventDefault();
              void search();
            }
          }}
          placeholder="Search address or place…"
          className="flex-1 bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="button"
          onClick={() => void search()}
          disabled={searching || !q.trim()}
          className="rounded-md bg-gray-700 hover:bg-gray-600 px-3 py-2 text-sm text-white disabled:opacity-40"
        >
          {searching ? "…" : "Search"}
        </button>
      </div>

      {error && <div className="text-xs text-amber-400">{error}</div>}

      {results.length > 1 && (
        <div className="max-h-28 overflow-auto rounded border border-gray-700 divide-y divide-gray-700">
          {results.map((r, i) => (
            <button
              key={i}
              type="button"
              onClick={() =>
                onPick(roundCoord(Number(r.lat)), roundCoord(Number(r.lon)))
              }
              className="block w-full text-left px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-700/50"
            >
              {r.display_name}
            </button>
          ))}
        </div>
      )}

      <div className="relative isolate z-0 rounded-lg overflow-hidden border border-gray-700" style={{ height: heightPx }}>
        <MapContainer center={center} zoom={lat != null ? 9 : 2} style={{ height: "100%", width: "100%" }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            className="osm-tiles-dark"
          />
          <ClickToPick onPick={onPick} />
          <Recenter lat={lat} lon={lon} />
          {lat != null && lon != null && (
            <Marker
              position={[lat, lon]}
              icon={pinIcon}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const p = (e.target as L.Marker).getLatLng();
                  onPick(roundCoord(p.lat), roundCoord(wrapLon(p.lng)));
                },
              }}
            />
          )}
        </MapContainer>
      </div>
      <p className="text-xs text-gray-500">
        Click the map or drag the pin to place the station, or search an address above.
      </p>
    </div>
  );
};

export default LocationPickerMap;
